import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import clsx from "clsx";
import {
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  LinearProgress,
} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";

import apiClient from "../../../auth/apiClient";
import FacilityService from "../../../services/FacilityService";
import Currency from "../../Currency";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import { useStyles } from "./styles";

const MIN_SEARCH_LENGTH = 3;
const MAX_SEARCH_LENGTH = 200;
const ROWS_PER_PAGE = 15;
const DEFAULT_ORDER_BY = "netRevenueAmount";
const DEFAULT_ORDER = "desc";

const facilityService = new FacilityService(apiClient);

const COLUMNS = [
  { id: "name", label: "Name", numeric: false },
  {
    id: "deviceCount",
    label: "Device Count",
    numeric: true,
    tooltip:
      "Displays the device count of the facility regardless of their online/offline status",
  },
  {
    id: "revenueTransactionCount",
    label: "Revenue Transactions",
    numeric: true,
    tooltip: "Displays revenue transaction counts from the last 30 days",
  },
  {
    id: "contractEntryTransactionCount",
    label: "Contract Entry Transactions",
    numeric: true,
    tooltip:
      "Displays the contract entry transaction counts from the last 30 days",
  },
  {
    id: "netRevenueAmount",
    label: "Net Revenue",
    numeric: true,
    tooltip:
      "Displays the net revenue collected at the facility from the last 30 days",
  },
];

const compareValues = (a, b, orderBy, numeric) => {
  if (numeric) return (a[orderBy] || 0) - (b[orderBy] || 0);
  return (a[orderBy] ?? "").toString().toLowerCase().localeCompare(
    (b[orderBy] ?? "").toString().toLowerCase()
  );
};

const sortRows = (rows, order, orderBy, numeric) =>
  [...rows].sort((a, b) => {
    const cmp = compareValues(a, b, orderBy, numeric);
    return order === "desc" ? -cmp : cmp;
  });

const FacilityActivity = () => {
  const classes = useStyles();
  const isAdmin = useSelector((state) => state?.user?.isAdmin);
  const enqueueSnackbar = useEnqueueSnackbar();

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [page, setPage] = useState(1);

  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);
  const enqueueSnackbarRef = useRef(enqueueSnackbar);

  useEffect(() => {
    enqueueSnackbarRef.current = enqueueSnackbar;
  }, [enqueueSnackbar]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadFacilityActivity = useCallback(async (term) => {
    const currentId = ++requestIdRef.current;
    setIsLoading(true);
    try {
      const result = await facilityService.getFacilityActivity(term ?? "");
      if (!isMountedRef.current || currentId !== requestIdRef.current) return;
      setRows(Array.isArray(result?.data) ? result.data : []);
    } catch (err) {
      if (!isMountedRef.current || currentId !== requestIdRef.current) return;
      setRows([]);
      enqueueSnackbarRef.current("Failed to load facility activity", {
        variant: "error",
        tag: "FacilityActivityLoadError",
      });
    } finally {
      if (isMountedRef.current && currentId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    loadFacilityActivity(activeSearchTerm);
  }, [isAdmin, activeSearchTerm, loadFacilityActivity]);

  const handleRequestSort = (column) => {
    if (orderBy !== column.id) {
      setOrderBy(column.id);
      setOrder(column.numeric ? "desc" : "asc");
    } else {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    }
    setPage(1);
  };

  const trimmedInput = searchInput.trim();
  const isSearchDisabled =
    isLoading ||
    trimmedInput.length < MIN_SEARCH_LENGTH ||
    trimmedInput.length > MAX_SEARCH_LENGTH;

  const runSearch = () => {
    if (isSearchDisabled) return;
    setPage(1);
    setActiveSearchTerm(trimmedInput);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    if (activeSearchTerm) {
      setPage(1);
      setActiveSearchTerm("");
    }
  };

  const sortedRows = useMemo(() => {
    const numeric = COLUMNS.find((c) => c.id === orderBy)?.numeric ?? false;
    return sortRows(rows, order, orderBy, numeric);
  }, [rows, order, orderBy]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / ROWS_PER_PAGE));
  const safePage = Math.min(page, pageCount);
  const pagedRows = sortedRows.slice(
    (safePage - 1) * ROWS_PER_PAGE,
    safePage * ROWS_PER_PAGE
  );

  if (!isAdmin) return null;

  return (
    <Paper className={classes.root} elevation={0} data-testid="facility-activity">
      <Grid container className={clsx("facility-activity-panel", classes.cardHeader)}>
        <Grid item xs={12} className={classes.cardHeaderTitle}>
          <Typography className={clsx("title")}>Facility Activity</Typography>
        </Grid>
      </Grid>

      <Grid container className={clsx("table-container", classes.tableContainer)}>
        <div className={classes.toolbar}>
          <Grid container spacing={2} className={classes.searchRow} alignItems="flex-start">
            <Grid item xs={12} sm={8} md={6} lg={5}>
              <TextField
                id="facility-activity-search"
                data-testid="facility-activity-search"
                label="Search for..."
                variant="outlined"
                fullWidth
                value={searchInput}
                onChange={(e) => setSearchInput((e.target.value ?? "").slice(0, MAX_SEARCH_LENGTH))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    runSearch();
                  }
                }}
                helperText={`minimum ${MIN_SEARCH_LENGTH} characters`}
                inputProps={{ maxLength: MAX_SEARCH_LENGTH }}
                className={classes.searchField}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={3} className={classes.searchButtonCell}>
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={runSearch}
                disabled={isSearchDisabled}
                data-testid="facility-activity-search-btn"
                className={classes.searchButton}
              >
                Search
              </Button>
              {activeSearchTerm && (
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleClearSearch}
                  data-testid="facility-activity-search-clear"
                  className={classes.clearButton}
                >
                  Clear
                </Button>
              )}
            </Grid>
          </Grid>

          {!isLoading && sortedRows.length > ROWS_PER_PAGE && (
            <div className={classes.paginationCell}>
              <Pagination
                className={clsx("table-pagination", classes.pagination)}
                page={safePage}
                onChange={(_e, newPage) => setPage(newPage)}
                count={pageCount}
                color="primary"
                shape="rounded"
              />
            </div>
          )}
        </div>

        {isLoading && (
          <LinearProgress className={classes.loadingBar} data-testid="loading-progress" />
        )}

        <Table className={clsx("table", classes.table)}>
          <TableHead>
            <TableRow className={clsx("header-row", classes.tableHeader)}>
              {COLUMNS.map((column) => {
                const isActive = orderBy === column.id;
                const sortLabel = (
                  <TableSortLabel
                    active={isActive}
                    direction={isActive ? order : column.numeric ? "desc" : "asc"}
                    onClick={() => handleRequestSort(column)}
                    className={clsx({ [classes.numericSortLabel]: column.numeric })}
                    data-testid={`sort-${column.id}`}
                  >
                    {column.label}
                  </TableSortLabel>
                );

                return (
                  <TableCell
                    key={column.id}
                    className={column.numeric ? classes.numericHeaderCell : classes.headerCell}
                    align={column.numeric ? "right" : "left"}
                    sortDirection={isActive ? order : false}
                  >
                    {column.tooltip ? (
                      <Tooltip title={column.tooltip} arrow placement="top">
                        <span>{sortLabel}</span>
                      </Tooltip>
                    ) : (
                      sortLabel
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          {!isLoading && pagedRows.length === 0 ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={COLUMNS.length}>
                  <Typography
                    className={clsx(
                      "no-data-message",
                      classes.centerContentColumn,
                      classes.noDataMessage
                    )}
                  >
                    There is no data to display.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody data-testid="table-body">
              {pagedRows.map((row) => (
                <TableRow
                  key={row.facilityID || row.name}
                  className={clsx(`${row.name}-table-row`, classes.tableRow)}
                  data-testid={`facility-row-${row.facilityID || row.name}`}
                >
                  <TableCell className={classes.tableCell}>
                    <Typography>{row.name}</Typography>
                  </TableCell>
                  <TableCell className={classes.numericCell}>
                    <Typography>{(row.deviceCount || 0).toLocaleString("en-US")}</Typography>
                  </TableCell>
                  <TableCell className={classes.numericCell}>
                    <Typography>{(row.revenueTransactionCount || 0).toLocaleString("en-US")}</Typography>
                  </TableCell>
                  <TableCell className={classes.numericCell}>
                    <Typography>{(row.contractEntryTransactionCount || 0).toLocaleString("en-US")}</Typography>
                  </TableCell>
                  <TableCell className={classes.numericCell}>
                    <Typography><Currency value={row.netRevenueAmount || 0} /></Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </Grid>
    </Paper>
  );
};

export default FacilityActivity;
